import { useState } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import { useCreateTeamsBulk } from "../../data/useCreateTeamsBulk";
import { useUpdateTeam } from "../../data/useUpdateTeam";
import { useDivisions } from "../../data/useDivisions";
import { useUsers } from "../../data/useUsers";
import { useUpdateUserDetails } from "../../data/useUpdateUserDetails";
import { queryClient } from "../../queryClient";
import { applyServerFieldErrors, showError } from "../../utils/error-handling";
import { notify } from "../../utils/notify";
import { Box } from "../Box";
import { Button } from "../Button";
import { Input } from "../Input";
import { Loader } from "../Loader";
import { Paragraph } from "../Paragraph";
import { Slider } from "../Slider";
import { Toggle } from "../Toggle";
import { Flex } from "../Flex";
import { ProfileOrb } from "../ProfileOrb";
import CustomTabs from "../CustomTabs";
import Select from "../Select";
import { ConfirmModal } from "./ConfirmModal";
import { Team } from "../../types/Team";
import { User } from "../../types/User";
import { addTeamValidation } from "../../utils/validationSchema";
import { useAuth } from "../../context/AuthContext";

type AddEditTeamModalProps = {
  isOpen: boolean;
  onRequestClose: () => void;
  team?: Team | null;
  /** Preselects and locks the Division when adding a Team from within a Division's row. */
  divisionId?: number;
};

const DetailsTab = ({
  team,
  divisionId,
  onSaved,
}: {
  team?: Team | null;
  divisionId?: number;
  onSaved: () => void;
}) => {
  const mode = team ? "edit" : "create";
  const { hasPermission } = useAuth();

  const { data: divisions } = useDivisions();
  const divisionOptions = divisions?.data
    ?.filter(({ is_active }) => is_active)
    .map(({ id, display_name }) => ({ label: display_name, value: id }));

  const { mutate: createTeamsBulk, isLoading: isLoadingCreate } =
    useCreateTeamsBulk();
  const { mutate: updateTeam, isLoading: isLoadingUpdate } = useUpdateTeam();

  const isLoadingAction = isLoadingCreate || isLoadingUpdate;

  const handleSubmit = (values: any, { setErrors }: FormikHelpers<any>) => {
    const payload = {
      name: values.name.trim(),
      display_name: values.display_name.trim(),
      division: values.division,
      is_active: mode === "create" ? true : values.is_active,
    };

    const onComplete = () => {
      queryClient.invalidateQueries(["teams"]);
      onSaved();
    };

    const fieldNames = ["name", "display_name", "division"];

    if (mode === "edit") {
      updateTeam(
        { id: team!.id, ...payload },
        {
          onSuccess: () => {
            notify("Team updated", { type: "success" });
            onComplete();
          },
          onError: (err: any) =>
            applyServerFieldErrors(
              err,
              fieldNames,
              setErrors,
              "A problem occurred while updating the Team."
            ),
        }
      );
    } else {
      createTeamsBulk([payload], {
        onSuccess: () => {
          notify("Team created", { type: "success" });
          onComplete();
        },
        onError: (err: any) =>
          applyServerFieldErrors(
            err,
            fieldNames,
            setErrors,
            "A problem occurred while creating the Team."
          ),
      });
    }
  };

  return (
    <Formik
      initialValues={{
        name: team?.name ?? "",
        display_name: team?.display_name ?? "",
        division: team?.division ?? divisionId ?? "",
        is_active: team?.is_active ?? true,
      }}
      validationSchema={addTeamValidation}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formikProps) => (
        <Form onSubmit={formikProps.handleSubmit}>
          <Select
            onChange={(option: any) =>
              formikProps.setFieldValue("division", option.value)
            }
            value={divisionOptions?.find(
              ({ value }: any) => value === formikProps.values.division
            )}
            options={divisionOptions}
            placeholder="Choose division"
            mb={3}
            label="Division"
            name="division"
            isRequired
            isDisabled={mode === "edit"}
            hasError={Boolean(formikProps.errors.division)}
            errorMessage={formikProps.errors.division as string}
          />
          <Input
            onChange={formikProps.handleChange}
            value={formikProps.values.name}
            placeholder="Enter team name"
            mb={3}
            label="Name"
            name="name"
            isRequired
            hasError={Boolean(formikProps.errors.name)}
            errorMessage={formikProps.errors.name as string}
          />
          <Input
            onChange={formikProps.handleChange}
            value={formikProps.values.display_name}
            placeholder="Enter display name"
            mb={3}
            label="Display name"
            name="display_name"
            isRequired
            hasError={Boolean(formikProps.errors.display_name)}
            errorMessage={formikProps.errors.display_name as string}
          />

          {mode === "edit" && (
            <Flex alignItems="center" gap="8" mb={3}>
              <Paragraph m="0px">Active</Paragraph>
              <Toggle
                isChecked={formikProps.values.is_active}
                onChange={(e) =>
                  formikProps.setFieldValue("is_active", e.target.checked)
                }
              />
            </Flex>
          )}

          <Button
            type="submit"
            size="large"
            width="100%"
            mt="4"
            mb="0"
            isDisabled={
              !formikProps.dirty ||
              (formikProps.dirty && !formikProps.isValid) ||
              isLoadingAction ||
              !hasPermission("teams.manage")
            }
            title={
              hasPermission("teams.manage")
                ? undefined
                : "You don't have permission to manage teams"
            }
          >
            {mode === "edit" ? "Save" : "Create Team"}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

const MembersTab = ({ team }: { team: Team }) => {
  const { hasPermission } = useAuth();
  const [search, setSearch] = useState("");
  const [pendingToggle, setPendingToggle] = useState<
    { user: User; isMember: boolean } | null
  >(null);
  // Unfiltered: every tenant user, to toggle membership against.
  const { data: allUsers, isLoading: isLoadingAll } = useUsers();
  // Filtered: who's already in this Team, to know which toggles start on.
  const { data: teamUsers, isLoading: isLoadingTeam } = useUsers({
    division: team.division,
    team: team.id,
  });
  const { mutate: updateUser } = useUpdateUserDetails();

  const isLoading = isLoadingAll || isLoadingTeam;
  const memberIds = new Set(
    (teamUsers?.data?.results ?? []).map(({ id }) => id)
  );

  const users = (allUsers?.data?.results ?? []).filter((user) =>
    `${user.first_name} ${user.last_name} ${user.email}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const toggleMember = (user: User, isMember: boolean) => {
    const currentTeamIds = (user.team ?? []).map(({ id }) => id);
    const currentActiveTeamIds = (user.active_team ?? []).map(({ id }) => id);
    const currentDivisionIds = (user.division ?? []).map(({ id }) => id);
    const currentActiveDivisionIds = (user.active_division ?? []).map(
      ({ id }) => id
    );

    const nextTeamIds = isMember
      ? Array.from(new Set([...currentTeamIds, team.id]))
      : currentTeamIds.filter((id) => id !== team.id);
    const nextActiveTeamIds = isMember
      ? Array.from(new Set([...currentActiveTeamIds, team.id]))
      : currentActiveTeamIds.filter((id) => id !== team.id);
    // A Team membership implies Division access; adding never removes the Division.
    const nextDivisionIds = isMember
      ? Array.from(new Set([...currentDivisionIds, team.division]))
      : currentDivisionIds;
    const nextActiveDivisionIds = isMember
      ? Array.from(new Set([...currentActiveDivisionIds, team.division]))
      : currentActiveDivisionIds;

    updateUser(
      {
        id: user.id,
        team: nextTeamIds,
        active_team: nextActiveTeamIds,
        division: nextDivisionIds,
        active_division: nextActiveDivisionIds,
      },
      {
        onSuccess: () => {
          notify(
            `${user.first_name} ${
              isMember ? "added to" : "removed from"
            } ${team.display_name}`,
            { type: "success" }
          );
          queryClient.invalidateQueries(["users"]);
        },
        onError: (err: any) =>
          showError(err, "A problem occurred while updating team membership."),
      }
    );
  };

  if (isLoading) {
    return (
      <Flex justifyContent="center" p={4}>
        <Loader />
      </Flex>
    );
  }

  return (
    <Box mt={3}>
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <Input
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          type="search"
          size="small"
          placeholder="Search"
          maxWidth={200}
        />
        <Paragraph color="gray.50" m="0px">
          {memberIds.size} Member{memberIds.size === 1 ? "" : "s"}
        </Paragraph>
      </Flex>

      {users.length === 0 && (
        <Paragraph color="gray.60" mt={3}>
          No users found.
        </Paragraph>
      )}

      {users.map((user) => (
        <Flex
          key={user.id}
          justifyContent="space-between"
          alignItems="center"
          py={2}
          borderBottom="1px solid"
          borderBottomColor="gray.10"
        >
          <Flex alignItems="center" gap="8">
            <ProfileOrb
              firstName={user.first_name}
              lastName={user.last_name}
              colour={user.colour}
            />
            <Box>
              <Paragraph m="0px" fontWeight="bold">
                {user.first_name} {user.last_name}
              </Paragraph>
              <Paragraph m="0px" color="gray.50" fontSize={1}>
                {user.email}
              </Paragraph>
            </Box>
          </Flex>
          <Flex alignItems="center" gap="16">
            <Paragraph m="0px" color="gray.60">
              {user.groups?.[0]?.name ?? "-"}
            </Paragraph>
            <Toggle
              isChecked={memberIds.has(user.id)}
              isDisabled={!hasPermission("assign.team")}
              title={
                hasPermission("assign.team")
                  ? undefined
                  : "You don't have permission to assign team membership"
              }
              onChange={(e) =>
                setPendingToggle({ user, isMember: e.target.checked })
              }
            />
          </Flex>
        </Flex>
      ))}

      <ConfirmModal
        isOpen={Boolean(pendingToggle)}
        onRequestClose={() => setPendingToggle(null)}
        handleConfirm={() => {
          if (pendingToggle) {
            toggleMember(pendingToggle.user, pendingToggle.isMember);
          }
          setPendingToggle(null);
        }}
        title={
          pendingToggle
            ? `${pendingToggle.isMember ? "Add" : "Remove"} ${
                pendingToggle.user.first_name
              } ${pendingToggle.isMember ? "to" : "from"} ${team.display_name}?`
            : undefined
        }
        content={
          pendingToggle?.isMember
            ? `${pendingToggle.user.first_name} will immediately be able to see everything tagged to this Team.`
            : pendingToggle
            ? `${pendingToggle.user.first_name} will immediately lose access to records tagged to this Team.`
            : undefined
        }
      />
    </Box>
  );
};

export const AddEditTeamModal = (props: AddEditTeamModalProps) => {
  const { isOpen, onRequestClose, team, divisionId } = props;
  const mode = team ? "edit" : "create";

  return (
    <Slider isOpen={isOpen} onRequestClose={onRequestClose} width="500px">
      <Box px={4} py={3} height="94%" overflowY="auto">
        <Paragraph fontWeight="bold" fontSize={3}>
          {mode === "edit" ? `Edit Team | ${team!.display_name}` : "New Team"}
        </Paragraph>
        <Box borderBottom="1px solid" borderBottomColor="gray.30" mb={3} />

        <CustomTabs
          showDividerBelowNav
          tabs={[
            {
              label: "Details",
              content: (
                <DetailsTab
                  team={team}
                  divisionId={divisionId}
                  onSaved={onRequestClose}
                />
              ),
            },
            {
              label: "Members",
              content: team ? (
                <MembersTab team={team} />
              ) : (
                <Paragraph color="gray.60" mt={3}>
                  Save the Team first, then come back here to manage its
                  Members.
                </Paragraph>
              ),
            },
          ]}
        />
      </Box>
    </Slider>
  );
};
